// Shared storefront constants + cart math.
// All money is stored in cents (Stripe convention). Display divides by 100.

// Florida sales tax is no longer charged — the festival is over and orders now
// ship out of state, so no in-person FL sale to tax. Kept as 0 (rather than
// deleted) so order docs keep a stable shape and older paid orders still read.
export const TAX_RATE = 0

// Post-festival shipping. One flat charge covers the whole order, priced by the
// bulkiest thing in the cart (single box) — the highest applicable rate wins,
// charges do NOT stack across categories:
//   • any sweats (cozy) present ....... $10 (biggest box)
//   • 3+ t-shirts ..................... $10
//   • 1–2 t-shirts .................... $6
//   • magazines (no apparel) .......... $6
//   • only rings/other ................ $0 (not specified — no shipping added)
export const SHIPPING_SWEATS_CENTS = 1000
export const SHIPPING_TSHIRT_BULK_CENTS = 1000 // 3+ tees
export const SHIPPING_TSHIRT_CENTS = 600 // 1–2 tees
export const SHIPPING_MAGAZINE_CENTS = 600
export const TSHIRT_BULK_THRESHOLD = 3

export const MERCH_GIFT_LABEL = '$75 Merch Credit for IABF Merch or Magazine Copies'
/** Prior-year merch gift label still recognized for credit. */
export const MERCH_GIFT_LEGACY_LABEL = '$100 IABF Merch & Magazine Issue #5 Credit'
export const MERCH_GIFT_CENTS = 7500

// The $100 welcome merch credit is retired — it's no longer applied to any
// order, and the storefront stops advertising it. Flip to true to re-enable it
// everywhere at once (cart math, server charge, hero + footnote messaging).
export const MERCH_CREDIT_ENABLED = false

// The dancer/faculty free t-shirt benefit is paused for now — everyone has
// already claimed theirs. The logic is intact; flip to true next year to bring
// back the $0 tees, the "claimed!" prompt, and the hero mention everywhere.
export const FREE_TSHIRT_ENABLED = false
export const SIZE_OPTIONS = ['XS', 'S', 'M', 'L', 'XL', '2XL']
export const RING_SIZE_OPTIONS = ['6', '7', '8', '9', '10', '11', '12', '13', '15']

// Item types and which ones use sizes / qualify for the free-t-shirt benefit.
export const ITEM_TYPES = ['tshirt', 'cozy', 'magazine', 'ring', 'other']
export const TYPES_WITH_SIZES = ['tshirt', 'cozy', 'ring']
export function typeHasSizes (type) { return TYPES_WITH_SIZES.includes(type) }
export function typeEligibleForFreeTshirt (type) { return type === 'tshirt' }
export function sizeOptionsFor (type) {
  if (type === 'ring') return RING_SIZE_OPTIONS
  return SIZE_OPTIONS
}

export function formatMoney (cents) {
  if (!Number.isFinite(cents)) cents = 0
  const dollars = cents / 100
  return `$${dollars.toFixed(2)}`
}

// Flat shipping for the whole cart. The free-t-shirt benefit and merch credit
// don't change what's physically boxed and mailed, so shipping is computed from
// gross quantities (the free tee is still shipped) and is never discounted.
export function computeShippingCents (lines) {
  const qtyOf = type => (lines || [])
    .filter(l => l && l.type === type && l.quantity > 0)
    .reduce((s, l) => s + l.quantity, 0)
  const sweatsQty = qtyOf('cozy')
  const tshirtQty = qtyOf('tshirt')
  const magazineQty = qtyOf('magazine')
  if (sweatsQty > 0) return SHIPPING_SWEATS_CENTS
  if (tshirtQty >= TSHIRT_BULK_THRESHOLD) return SHIPPING_TSHIRT_BULK_CENTS
  if (tshirtQty >= 1) return SHIPPING_TSHIRT_CENTS
  if (magazineQty > 0) return SHIPPING_MAGAZINE_CENTS
  return 0
}

export function dancerHasMerchGift (dancer) {
  if (!dancer) return false
  const gifts = [
    dancer.registrationGift1,
    dancer.registrationGift2,
    dancer.registrationGift3,
    dancer.registrationGift4,
    dancer.earlyBirdGift
  ]
  return gifts.some(g => g === MERCH_GIFT_LABEL || g === MERCH_GIFT_LEGACY_LABEL)
}

// Remaining cents on the $100 merch credit (after prior orders).
export function merchCreditRemainingCents (dancer) {
  if (!MERCH_CREDIT_ENABLED) return 0
  if (!dancerHasMerchGift(dancer)) return 0
  const used = Number(dancer.merchCreditUsedCents) || 0
  return Math.max(0, MERCH_GIFT_CENTS - used)
}

export function freeBenefitAlreadyUsed (dancer) {
  return !!(dancer && dancer.merchFreeBenefitUsed)
}

// Compute the totals for a cart. Returns a breakdown that callers render and
// that the Cloud Function also recomputes server-side before charging.
//
// cart: [{ itemId, name, type, unitPriceCents, quantity, size }]
//   - `name`, `type` are display only here; canonical values come from
//     Firestore on the server.
//
// dancer: { merchFreeBenefitUsed, merchCreditUsedCents, registrationGift1/2/3, earlyBirdGift }
// opts: { isFaculty } — when true, applies the free-tshirt benefit to a
// guest order (faculty don't have dancer accounts but are still entitled).
export function computeCartTotals (cart, dancer, opts = {}) {
  const lines = (cart || []).filter(l => l && l.quantity > 0).map(l => ({ ...l, lineTotalCents: l.unitPriceCents * l.quantity }))
  const subtotalCents = lines.reduce((s, l) => s + l.lineTotalCents, 0)

  // ── Free benefit: cheapest tshirt in the cart is free ───────────
  // Eligible: dancers who haven't claimed yet, OR faculty (validated by
  // the storefront against a code stored in iabf-config-2026/store).
  let freeBenefitCents = 0
  let freeBenefitLineId = null
  let freeBenefitKind = 'none'
  const dancerEligible = dancer && !freeBenefitAlreadyUsed(dancer)
  const facultyEligible = !dancer && !!opts.isFaculty
  if (FREE_TSHIRT_ENABLED && (dancerEligible || facultyEligible) && subtotalCents > 0) {
    const tshirtUnits = lines.filter(l => l.type === 'tshirt' && l.unitPriceCents > 0)
    if (tshirtUnits.length > 0) {
      const cheapest = tshirtUnits.reduce((a, b) => (a.unitPriceCents <= b.unitPriceCents ? a : b))
      freeBenefitCents = cheapest.unitPriceCents
      freeBenefitLineId = cheapest.itemId + '|' + (cheapest.size || '')
      freeBenefitKind = 'tshirt'
    }
  }

  // ── $100 merch credit (after free benefit) ──────────────────────
  const remainingAfterFreeBenefit = subtotalCents - freeBenefitCents
  const merchCreditAvailable = merchCreditRemainingCents(dancer)
  const merchCreditUsedNowCents = Math.max(0, Math.min(merchCreditAvailable, remainingAfterFreeBenefit))

  // ── Merch owed (after benefit + credit), shipping, total ────────
  // No FL sales tax post-festival. Shipping is added on top of the
  // discounted merch total and is not covered by benefits or credit.
  const taxableSubtotalCents = Math.max(0, subtotalCents - freeBenefitCents - merchCreditUsedNowCents)
  const taxCents = Math.round(taxableSubtotalCents * TAX_RATE) // 0 — tax removed
  const shippingCents = computeShippingCents(lines)
  const totalCents = taxableSubtotalCents + taxCents + shippingCents

  return {
    lines,
    subtotalCents,
    freeBenefitCents,
    freeBenefitKind,
    freeBenefitLineId,
    merchCreditAvailableCents: merchCreditAvailable,
    merchCreditUsedCents: merchCreditUsedNowCents,
    taxableSubtotalCents,
    taxRate: TAX_RATE,
    taxCents,
    shippingCents,
    totalCents
  }
}

// LocalStorage cart — one per browser. Earlier this was scoped by dancerId
// for safety on a shared device, but it caused a race when the cart page
// loaded before the dancer record did (items saved under null dancerId
// were unreachable). Keep it simple instead.
const STORAGE_KEY = 'iabfStoreCart2026'

export function loadCart () {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) return parsed
    // Backwards compat: { dancerId, cart } from the old scoped format.
    if (parsed && Array.isArray(parsed.cart)) return parsed.cart
    return []
  } catch (e) {
    return []
  }
}

export function saveCart (cart) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cart || []))
  } catch (e) { /* ignore — private mode etc. */ }
}

export function clearCart () {
  try { window.localStorage.removeItem(STORAGE_KEY) } catch (e) { /* noop */ }
}

// Faculty unlock code, persisted in localStorage so the second-tab cart page
// (and a return visit) still sees the entitlement. Cleared after a paid order.
const FACULTY_CODE_KEY = 'iabfStoreFacultyCode2026'

export function loadFacultyCode () {
  try { return window.localStorage.getItem(FACULTY_CODE_KEY) || '' } catch (e) { return '' }
}

export function saveFacultyCode (code) {
  try {
    if (code) window.localStorage.setItem(FACULTY_CODE_KEY, code)
    else window.localStorage.removeItem(FACULTY_CODE_KEY)
  } catch (e) { /* noop */ }
}

export function clearFacultyCode () {
  try { window.localStorage.removeItem(FACULTY_CODE_KEY) } catch (e) { /* noop */ }
}
